/**
 * FHIR Observation Validation Utility
 * 
 * Validates observation resources against FHIR R4 specification requirements
 * before submission to the FHIR server.
 */

// Required fields for a valid FHIR Observation
const REQUIRED_FIELDS = ['resourceType', 'status', 'code'];

// Valid observation statuses per FHIR spec
const VALID_STATUSES = [
  'registered',
  'preliminary',
  'final',
  'amended',
  'corrected',
  'cancelled',
  'entered-in-error',
  'unknown'
];

// LOINC code validation regex (numeric with optional hyphen and check digit)
const LOINC_REGEX = /^\d{1,5}-\d$/;

// Unit of measure validation (UCUM codes)
const VALID_UNIT_SYSTEMS = [
  'http://unitsofmeasure.org',
  'http://loinc.org'
];

/**
 * Validation result object
 */
class ValidationResult {
  constructor() {
    this.isValid = true;
    this.errors = [];
    this.warnings = [];
  }

  addError(field, message) {
    this.isValid = false;
    this.errors.push({ field, message });
  }

  addWarning(field, message) {
    this.warnings.push({ field, message });
  }
}

/**
 * Validates a FHIR Observation resource
 * 
 * @param {Object} observation - The FHIR Observation resource to validate
 * @returns {ValidationResult} - Validation result with errors and warnings
 */
export const validateObservation = (observation) => {
  const result = new ValidationResult();

  if (!observation || typeof observation !== 'object') {
    result.addError('observation', 'Observation must be a valid object');
    return result;
  }

  // Check resourceType
  if (observation.resourceType !== 'Observation') {
    result.addError('resourceType', `resourceType must be 'Observation', got '${observation.resourceType}'`);
  }

  // Check status
  if (!observation.status) {
    result.addError('status', 'status is required');
  } else if (!VALID_STATUSES.includes(observation.status)) {
    result.addError('status', `Invalid status '${observation.status}'. Must be one of: ${VALID_STATUSES.join(', ')}`);
  }

  // Check code
  if (!observation.code) {
    result.addError('code', 'code is required');
  } else {
    // Validate code structure
    if (!observation.code.text && !observation.code.coding) {
      result.addError('code', 'code must have either text or coding');
    }

    // Validate coding array if present
    if (observation.code.coding) {
      if (!Array.isArray(observation.code.coding)) {
        result.addError('code.coding', 'coding must be an array');
      } else {
        observation.code.coding.forEach((coding, index) => {
          if (!coding.code) {
            result.addWarning(`code.coding[${index}]`, 'coding should have a code value');
          }
          if (coding.system === 'http://loinc.org' && coding.code) {
            if (!LOINC_REGEX.test(coding.code)) {
              result.addWarning(`code.coding[${index}].code`, `'${coding.code}' may not be a valid LOINC code format`);
            }
          }
        });
      }
    }
  }

  // Validate valueQuantity if present
  if (observation.valueQuantity) {
    validateValueQuantity(observation.valueQuantity, result);
  }

  // Validate effectiveDateTime if present
  if (observation.effectiveDateTime) {
    const date = new Date(observation.effectiveDateTime);
    if (isNaN(date.getTime())) {
      result.addError('effectiveDateTime', 'effectiveDateTime must be a valid ISO date');
    } else if (date > new Date()) {
      result.addWarning('effectiveDateTime', 'effectiveDateTime is in the future');
    }
  }

  // Validate identifier if present
  if (observation.identifier) {
    if (!Array.isArray(observation.identifier)) {
      result.addError('identifier', 'identifier must be an array');
    } else {
      observation.identifier.forEach((id, index) => {
        if (!id.system && !id.value) {
          result.addWarning(`identifier[${index}]`, 'identifier should have system and/or value');
        }
      });
    }
  }

  // Validate category if present
  if (observation.category) {
    if (!Array.isArray(observation.category)) {
      result.addError('category', 'category must be an array');
    }
  }

  return result;
};

/**
 * Validates a valueQuantity object
 */
const validateValueQuantity = (valueQuantity, result) => {
  if (typeof valueQuantity !== 'object') {
    result.addError('valueQuantity', 'valueQuantity must be an object');
    return;
  }

  // Check value is a number
  if (valueQuantity.value !== undefined) {
    if (typeof valueQuantity.value !== 'number') {
      result.addError('valueQuantity.value', 'value must be a number');
    } else if (!isFinite(valueQuantity.value)) {
      result.addError('valueQuantity.value', 'value must be a finite number');
    }
  } else {
    result.addWarning('valueQuantity.value', 'valueQuantity should have a value');
  }

  // Check unit
  if (!valueQuantity.unit && !valueQuantity.code) {
    result.addWarning('valueQuantity.unit', 'valueQuantity should have a unit or code');
  }

  // Validate system if present
  if (valueQuantity.system && !VALID_UNIT_SYSTEMS.includes(valueQuantity.system)) {
    result.addWarning('valueQuantity.system', `Unknown unit system '${valueQuantity.system}'`);
  }
};

/**
 * Validates observation value ranges for common vital signs
 * 
 * @param {string} observationType - The type of observation
 * @param {number} value - The value to validate
 * @returns {Object} - { isValid, message }
 */
export const validateObservationRange = (observationType, value) => {
  const ranges = {
    'Heart Rate': { min: 20, max: 300, unit: 'bpm' },
    'Body Temperature': { min: 85, max: 110, unit: '°F' },
    'Oxygen Saturation': { min: 50, max: 100, unit: '%' },
    'Blood Glucose': { min: 20, max: 600, unit: 'mg/dL' },
    'Body Weight': { min: 0.5, max: 700, unit: 'kg' },
    'Steps Taken': { min: 0, max: 100000, unit: 'steps' },
    'Sleep Duration': { min: 0, max: 24, unit: 'hours' },
    'Systolic BP': { min: 50, max: 300, unit: 'mmHg' },
    'Diastolic BP': { min: 30, max: 200, unit: 'mmHg' },
    'Calories': { min: 0, max: 20000, unit: 'cal' },
    'Water Intake': { min: 0, max: 20, unit: 'liters' },
  };

  const range = ranges[observationType];
  if (!range) {
    return { isValid: true, message: null };
  }

  if (value < range.min || value > range.max) {
    return {
      isValid: false,
      message: `Value ${value} is outside typical range for ${observationType} (${range.min}-${range.max} ${range.unit})`
    };
  }

  return { isValid: true, message: null };
};

/**
 * Creates a valid FHIR Observation resource
 * 
 * @param {Object} params - Observation parameters
 * @returns {Object} - Valid FHIR Observation resource
 */
export const createObservationResource = ({
  userId,
  observationType,
  value,
  unit,
  unitCode,
  loincCode,
  displayName,
  effectiveDateTime,
  status = 'final'
}) => {
  const observation = {
    resourceType: 'Observation',
    status,
    identifier: [
      {
        system: 'https://fhir.mieros.cloud/fhir/ids',
        value: userId
      }
    ],
    category: [
      {
        coding: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/observation-category',
            code: 'vital-signs',
            display: 'Vital Signs'
          }
        ]
      }
    ],
    code: loincCode ? {
      coding: [
        {
          system: 'http://loinc.org',
          code: loincCode,
          display: displayName || observationType
        }
      ],
      text: observationType
    } : {
      text: observationType
    },
    effectiveDateTime: effectiveDateTime || new Date().toISOString(),
    valueQuantity: {
      value: parseFloat(value),
      unit: unit,
      system: 'http://unitsofmeasure.org',
      code: unitCode || unit
    }
  };

  return observation;
};

/**
 * Validates and returns errors as a formatted string
 * 
 * @param {Object} observation - The observation to validate
 * @returns {string|null} - Error message or null if valid
 */
export const getValidationErrorMessage = (observation) => {
  const result = validateObservation(observation);
  
  if (result.isValid) {
    return null;
  }

  return result.errors.map(e => `${e.field}: ${e.message}`).join('; ');
};

export default {
  validateObservation,
  validateObservationRange,
  createObservationResource,
  getValidationErrorMessage
};
